/**
 * "Record for" — which unit the money belongs to (iter 612).
 *
 * An HQ or province officer records a donation or an expense that belongs to
 * an assembly, not to HQ. The server decides the list (own unit and the units
 * beneath it) and checks the same reach again on the write, so this is an
 * affordance, not the guard.
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Building2, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useRecordableUnits = () => {
  const { token } = useAuthStore();
  return useQuery({
    queryKey: ['recordable-units'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/admin/finance/recordable-units`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not load the units you may record for');
      return res.json();
    },
    enabled: !!token,
    staleTime: 60_000,
  });
};

export const RecordForUnit = ({ value, onChange, label = 'Record for' }) => {
  const { data, isLoading } = useRecordableUnits();
  const units = data?.units || [];

  React.useEffect(() => {
    if (!value && units.length === 1) onChange(units[0].id);
  }, [units, value, onChange]);

  if (isLoading) {
    return (
      <p className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your units…
      </p>
    );
  }
  if (!units.length) {
    return (
      <p className="text-sm text-amber-700" data-testid="record-for-none">
        You are not over any unit that can hold money yet — ask a super admin to
        attach you to a church or province.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-1.5">
        <Building2 className="h-3.5 w-3.5" /> {label} *
      </Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger data-testid="record-for-unit">
          <SelectValue placeholder="Choose the church this belongs to" />
        </SelectTrigger>
        <SelectContent>
          {units.map((u) => (
            <SelectItem key={u.id} value={u.id} data-testid={`record-for-${u.id}`}>
              {u.name}{u.is_own ? ' · your unit' : ''}
              {u.type ? ` (${u.type.replace(/_/g, ' ')})` : ''}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-slate-500">
        The money is recorded against this unit's books.
      </p>
    </div>
  );
};

export default RecordForUnit;
